import { useState, useEffect, useMemo, useContext, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { 
  ChevronLeft, 
  ChevronRight,
  Zap,
  TrendingUp,
  Trophy,
  BarChart3,
  Users,
  Award,
  Globe,
  Medal,
  BookOpen,
  Home,
  LayoutDashboard
} from "lucide-react";
import { cn } from "@/lib/utils";
import { scrollToSection } from "@/lib/scrollToSection";
import { useLanguage } from "@/contexts/LanguageContext";
import { AuthContext } from "@/contexts/AuthContext";

interface SectionItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  isRoute?: boolean;
}

const SectionSidebar = () => {
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [activeSection, setActiveSection] = useState<string>("");
  const { t } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const isHomePage = location.pathname === "/";
  const isDashboard = location.pathname === "/dashboard";
  
  // Use resilient auth access pattern
  const authContext = useContext(AuthContext);
  const user = authContext?.user ?? null;

  const sections: SectionItem[] = useMemo(() => {
    const baseSections: SectionItem[] = [
      { id: "hero", label: t.sidebar.home, icon: <Home className="w-4 h-4" /> },
      { id: "live-games", label: t.sidebar.liveResults, icon: <Zap className="w-4 h-4" /> },
      { id: "tournaments", label: t.sidebar.leagues, icon: <Trophy className="w-4 h-4" /> },
      { id: "betting-odds", label: t.sidebar.bettingOdds, icon: <TrendingUp className="w-4 h-4" /> },
      { id: "stats", label: t.sidebar.statistics, icon: <BarChart3 className="w-4 h-4" /> },
      { id: "players", label: t.sidebar.playerStats, icon: <Users className="w-4 h-4" /> },
      { id: "playoffs", label: t.sidebar.playoffBracket, icon: <Award className="w-4 h-4" /> },
      { id: "international-tournaments", label: t.sidebar.international, icon: <Globe className="w-4 h-4" /> },
      { id: "standings", label: t.sidebar.leagueStandings, icon: <Medal className="w-4 h-4" /> },
      { id: "rulebook", label: t.sidebar.hockeyRules, icon: <BookOpen className="w-4 h-4" /> },
    ];
    
    // Add My Page at the top if user is logged in
    if (user) {
      return [
        { id: "dashboard", label: t.navExtra.myPage, icon: <LayoutDashboard className="w-4 h-4" />, isRoute: true },
        ...baseSections
      ];
    }
    
    return baseSections;
  }, [user, t]);

  // Only observe sections when on homepage
  useEffect(() => {
    if (!isHomePage) return;
    
    const observers: IntersectionObserver[] = [];
    const sectionVisibility = new Map<string, boolean>();
    
    // Initialize visibility tracking
    sections.forEach(section => {
      sectionVisibility.set(section.id, false);
    });

    const updateActive = () => {
      // Find the first visible section from top
      for (const section of sections) {
        if (sectionVisibility.get(section.id)) {
          setActiveSection(section.id);
          break;
        }
      }
    };

    sections.forEach(section => {
      const element = document.getElementById(section.id);
      if (element) {
        const observer = new IntersectionObserver(
          (entries) => {
            entries.forEach(entry => {
              sectionVisibility.set(section.id, entry.isIntersecting);
            });
            updateActive();
          },
          {
            rootMargin: '-100px 0px -50% 0px',
            threshold: 0
          }
        );
        observer.observe(element);
        observers.push(observer);
      }
    });

    return () => {
      observers.forEach(observer => observer.disconnect());
    };
  }, [sections, isHomePage]);

  const handleNavigation = useCallback((section: SectionItem) => {
    // Handle route-based navigation (like My Page)
    if (section.isRoute) {
      navigate(`/${section.id}`);
      return;
    }
    
    // Handle section scrolling
    if (isHomePage) {
      // On homepage, use robust scroll utility for lazy-loaded sections
      scrollToSection(section.id, { maxAttempts: 30, pollInterval: 80 });
    } else {
      // On other pages, navigate to homepage then scroll after mount
      navigate(`/`);
      // Use a short delay to let the homepage mount, then scroll
      setTimeout(() => {
        scrollToSection(section.id, { maxAttempts: 40, pollInterval: 100 });
      }, 100);
    }
  }, [isHomePage, navigate]);

  return (
    <div
      className={cn(
        "fixed left-0 top-1/2 -translate-y-1/2 z-40 transition-all duration-300 ease-in-out hidden lg:block",
        isCollapsed ? "w-12" : "w-56"
      )}
    >
      <div className="relative bg-card/95 backdrop-blur-md border border-border/50 rounded-r-2xl shadow-xl overflow-hidden">
        {/* Toggle Button */}
        <button
          onClick={() => setIsCollapsed(!isCollapsed)}
          className="absolute -right-0 top-1/2 -translate-y-1/2 translate-x-1/2 z-10 bg-primary text-primary-foreground rounded-full p-2.5 shadow-lg hover:bg-primary/90 transition-colors"
          aria-label={isCollapsed ? "Expand navigation sidebar" : "Collapse navigation sidebar"}
        >
          {isCollapsed ? (
            <ChevronRight className="w-4 h-4" />
          ) : (
            <ChevronLeft className="w-4 h-4" />
          )}
        </button>

        {/* Header */}
        <div className={cn(
          "border-b border-border/50 p-3",
          isCollapsed ? "text-center" : ""
        )}>
          {isCollapsed ? (
            <div className="w-6 h-6 mx-auto bg-primary/20 rounded-lg flex items-center justify-center">
              <Trophy className="w-3.5 h-3.5 text-primary" />
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 bg-primary/20 rounded-lg flex items-center justify-center">
                <Trophy className="w-4 h-4 text-primary" />
              </div>
              <span className="text-sm font-bold text-foreground">{t.sidebar.quickNav}</span>
            </div>
          )}
        </div>

        {/* Navigation Items */}
        <nav className="p-2 max-h-[60vh] overflow-y-auto scrollbar-thin">
          <ul className="space-y-1">
            {sections.map((section) => {
              const isActive = section.isRoute 
                ? (section.id === "dashboard" && isDashboard)
                : activeSection === section.id;
              
              return (
                <li key={section.id}>
                  <button
                    onClick={() => handleNavigation(section)}
                    className={cn(
                      "w-full flex items-center gap-3 px-2.5 py-2 rounded-lg text-sm transition-all duration-200",
                      isActive
                        ? "bg-primary/20 text-primary font-medium"
                        : "text-muted-foreground hover:bg-muted hover:text-foreground",
                      isCollapsed && "justify-center px-2"
                    )}
                    title={isCollapsed ? section.label : undefined}
                  >
                    <span className={cn(
                      "flex-shrink-0 transition-colors",
                      isActive && "text-primary"
                    )}>
                      {section.icon}
                    </span>
                    {!isCollapsed && (
                      <span className="truncate">{section.label}</span>
                    )}
                    {isActive && !isCollapsed && (
                      <span className="ml-auto w-1.5 h-1.5 bg-primary rounded-full" />
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* Footer hint */}
        {!isCollapsed && (
          <div className="border-t border-border/50 p-3">
            <p className="text-[10px] text-muted-foreground text-center">
              Click to navigate
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SectionSidebar;
